import React, { useEffect, useCallback, useReducer, useRef } from "react";
import CustomButton from "../../assets/components/CustomButton";
import AnimatedPageContainer from "../../assets/containers/AnimatedPageContainer";
import appApi from "../../assets/utils/AppApi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import errorHandler from "../../assets/utils/ErrorHandler";
import Loading from "../../assets/containers/loadingDialog";
import toast from "react-hot-toast";
import TextFieldElement from "./TextFieldElement";
import TextAreaElement from "./TextAreaElement";
import FormFileUploader from "./FormFileUpload";
import CheckFieldsElement from "./CheckFieldsElement";
import RadioFieldsElement from "./RadioButtonFieldsElement";
import { FaPen } from "react-icons/fa";
import { MdAccountBalance } from "react-icons/md";
import classes from "./styles/style.module.css";
import { baseURL } from "../../assets/constants/baseUrl";
import { useSelector } from "react-redux";
import CustomInput from "../../assets/components/customInput";
import TitleElement from "./FormFiledTitle";
import ListAltIcon from "@material-ui/icons/ListAlt";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import HelpIcon from "@material-ui/icons/Help";
import ImportContactsIcon from "@material-ui/icons/ImportContacts";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import PermIdentityIcon from "@material-ui/icons/PermIdentity";
import PostAddIcon from "@material-ui/icons/PostAdd";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CreateOutlinedIcon from "@material-ui/icons/CreateOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import ContactSupportOutlinedIcon from "@material-ui/icons/ContactSupportOutlined";
import PermIdentityOutlinedIcon from "@material-ui/icons/PermIdentityOutlined";
import LocalLibraryOutlinedIcon from "@material-ui/icons/LocalLibraryOutlined";
import BookmarkBorderOutlinedIcon from "@material-ui/icons/BookmarkBorderOutlined";
import TimerIcon from "../../assets/images/icons/timer.svg";
import ProgressBar from "../../assets/components/ProgressBar";
import { createExamDeadline, getRemainingSeconds } from "./examTimer";

const initialState = {
  loading: false,
  formFields: [],
  formValues: {},
  savedAnswers: {},
  loadingSubmit: false,
  formId: "",
  formTitle: "",
  formSubTitle: "",
  formIcon: "",
  formPoster: "",
  formDescription: "",
  showMessage: false,
  formType: "",
  examTicket: null,
  examDeadline: null,
  remainingSeconds: null,
  examExpired: false,
  saveStatus: "idle",
};

function formReducer(state, action) {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_FORM_DATA":
      return {
        ...state,
        formFields: action.payload.fields,
        formValues: action.payload.initialValues,
        savedAnswers: action.payload.examTicket ? action.payload.initialValues : {},
        formId: action.payload.formId,
        formTitle: action.payload.title,
        formSubTitle: action.payload.subtitle,
        formIcon: action.payload.icon,
        formPoster: action.payload.poster,
        formDescription: action.payload.description,
        formType: action.payload.formType,
        examTicket: action.payload.examTicket,
        examDeadline: action.payload.examDeadline,
        remainingSeconds: action.payload.remainingSeconds,
        examExpired: action.payload.examExpired,
        saveStatus: "idle",
      };
    case "SET_LOADING_SUBMIT":
      return { ...state, loadingSubmit: action.payload };
    case "SET_SHOW_MESSAGE":
      return { ...state, showMessage: action.payload };
    case "UPDATE_FORM_VALUE":
      return {
        ...state,
        formValues: {
          ...state.formValues,
          [action.fieldId]: action.value,
        },
        savedAnswers: { ...state.savedAnswers, [action.fieldId]: "" },
      };
    case "SET_SAVED_ANSWER":
      return { ...state, savedAnswers: { ...state.savedAnswers, [action.fieldId]: action.value } };
    case "SET_REMAINING_SECONDS":
      return {
        ...state,
        remainingSeconds: action.payload,
        examExpired: action.payload <= 0,
      };
    case "SET_SAVE_STATUS":
      return { ...state, saveStatus: action.payload };
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

const FillForm = () => {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const saveTimers = useRef({});
  const answerSaves = useRef({});
  const answerVersions = useRef({});
  const formValues = useRef({});
  const saveSession = useRef(null);
  const submitting = useRef(false);
  const submitRef = useRef(null);
  const expirySubmitted = useRef(false);

  const preview = searchParams.get("preview");
  const { currentUser } = useSelector(({ user }) => user);

  const getForm = useCallback(async () => {
    const session = {};
    saveSession.current = session;
    Object.values(saveTimers.current).forEach(clearTimeout);
    saveTimers.current = {};
    answerSaves.current = {};
    answerVersions.current = {};
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const { data } = await appApi.get(
        `/form/getuserbyid/${params.id}${preview ? "?preview=" + preview : ""}`
      );
      if (session !== saveSession.current) return;
      let examTicket = null;
      let examDeadline = null;
      if (data.type === "Exam" && preview !== "true") {
        const ticketResponse = await appApi.post("/formticket/create", {
          form: data._id,
        });
        if (session !== saveSession.current) return;
        examTicket = ticketResponse.data;
        examDeadline = createExamDeadline(
          examTicket, performance.now(), examTicket.serverNow ?? ticketResponse.headers?.date
        );
        expirySubmitted.current = false;

        if (examTicket.formFilled) {
          navigate(`/formQR/${examTicket.formFilled}`, { replace: true });
          return;
        }
      }

      const initialValues = {};
      data.fields.forEach((field) => {
        initialValues[field._id] = "";
      });
      examTicket?.answers?.forEach((answer) => {
        initialValues[answer.formFieldId] = answer.userInput;
      });

      const remainingSeconds = getRemainingSeconds(examDeadline, performance.now());
      if (session !== saveSession.current) return;
      formValues.current = { ...initialValues };
      dispatch({
        type: "SET_FORM_DATA",
        payload: {
          fields: data.fields,
          initialValues,
          formId: data._id,
          title: data.title,
          subtitle: data.subtitle,
          icon: data.icon,
          poster: data.poster,
          description: data.description,
          formType: data.type,
          examTicket,
          examDeadline,
          remainingSeconds,
          examExpired: examTicket?.status === "expired" || remainingSeconds === 0,
        },
      });
      dispatch({ type: "SET_LOADING", payload: false });
    } catch (error) {
      if (session !== saveSession.current) return;
      errorHandler(error);
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [navigate, params.id, preview]);

  useEffect(() => {
    getForm();
  }, [getForm]);

  useEffect(() => {
    if (state.examDeadline === null) return;

    const updateTimer = () => {
      const remaining = getRemainingSeconds(state.examDeadline, performance.now());
      dispatch({ type: "SET_REMAINING_SECONDS", payload: remaining });

      if (remaining === 0 && !expirySubmitted.current) {
        expirySubmitted.current = true;
        submitRef.current?.();
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [state.examDeadline]);

  useEffect(() => {
    return () => {
      saveSession.current = null;
      Object.values(saveTimers.current).forEach(clearTimeout);
    };
  }, []);

  const saveAnswer = (id, value, version) => {
    const session = saveSession.current;
    const ticketId = state.examTicket._id;
    // Serialize each question's writes so an older response cannot overwrite a newer answer.
    const pending = (answerSaves.current[id] || Promise.resolve()).then(async () => {
      if (session !== saveSession.current) return false;
      dispatch({ type: "SET_SAVE_STATUS", payload: "saving" });
      try {
        await appApi.patch(`/formticket/${ticketId}/answer`, {
          formFieldId: id,
          userInput: value,
        });
        if (session !== saveSession.current) return false;
        if (answerVersions.current[id] === version) {
          dispatch({ type: "SET_SAVED_ANSWER", fieldId: id, value });
        }
        dispatch({ type: "SET_SAVE_STATUS", payload: "saved" });
        return true;
      } catch (error) {
        if (session !== saveSession.current) return false;
        if (answerVersions.current[id] === version) {
          formValues.current[id] = "";
          dispatch({ type: "UPDATE_FORM_VALUE", fieldId: id, value: "" });
        }
        dispatch({ type: "SET_SAVE_STATUS", payload: "failed" });
        const response = error.response?.data;
        toast.error(response?.msgFa || response?.messageFa || response?.message || response?.msg || "ذخیره پاسخ انجام نشد؛ دوباره تلاش کنید");
        return false;
      }
    }).finally(() => {
      if (session === saveSession.current && answerSaves.current[id] === pending) delete answerSaves.current[id];
    });
    answerSaves.current[id] = pending;
    return pending;
  };

  const handleInputChange = (id, value) => {
    if (submitting.current || state.examExpired) return;
    formValues.current[id] = value;
    const version = (answerVersions.current[id] || 0) + 1;
    answerVersions.current[id] = version;
    dispatch({ type: "UPDATE_FORM_VALUE", fieldId: id, value });

    const field = state.formFields.find((item) => item._id === id);
    if (!state.examTicket || !field?.details.examQuestion || state.examExpired) return;

    clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => saveAnswer(id, value, version), 500);
  };

  const handleSubmit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      dispatch({ type: "SET_LOADING_SUBMIT", payload: true });

      Object.values(saveTimers.current).forEach(clearTimeout);
      saveTimers.current = {};

      if (state.examTicket && !state.examExpired) {
        const pendingResults = await Promise.all(Object.values(answerSaves.current));
        if (pendingResults.some(saved => !saved)) return;
        const examAnswers = state.formFields
          .filter((field) => field.details.examQuestion)
          .map((field) =>
            saveAnswer(field._id, formValues.current[field._id] || "", answerVersions.current[field._id])
          );
        const results = await Promise.all(examAnswers);
        if (results.some(saved => !saved)) return;
      }

      const submissionData = {
        form: state.formId,
        fields: Object.keys(formValues.current).map(key => ({
          userInput: formValues.current[key],
          formFieldId: key,
        })),
      };
      const { data } = await appApi.post("/formfilled/create", submissionData);
      toast.success("با موفقیت ثبت شد");
      if (data.form.confirmation === "Message") {
        navigate(`/formQR/${data.formFilled._id}`, {
          replace: true,
          state: { showMessage: true },
        });
      } else if (data.form.confirmation === "QRCode") {
        navigate(`/formQR/${data.formFilled._id}`, {
          replace: true,
          state: { showMessage: false },
        });
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      submitting.current = false;
      dispatch({ type: "SET_LOADING_SUBMIT", payload: false });
    }
  };

  submitRef.current = handleSubmit;

  const formatRemainingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
    const remaining = (seconds % 60).toString().padStart(2, "0");
    return `${minutes}:${remaining}`;
  };

  const getRemainingTimeProgress = () => {
    const totalSeconds = state.examTicket?.durationMinutes * 60;
    if (!totalSeconds || state.remainingSeconds === null) return 0;
    return Math.max(0, Math.min(100, (state.remainingSeconds / totalSeconds) * 100));
  };

  const isFieldVisible = item => {
    if (!item.details.visibilityCondition) return true;
    const source = state.formFields[item.details.visibilityConditionSource];
    if (!source) return false;
    const value = state.formValues[source._id] || "";
    return source.details.type === "CheckBox"
      ? value.split("_").includes(item.details.visibilityConditionValue)
      : value === item.details.visibilityConditionValue;
  };
  const availableQuestions = state.formFields.filter(field =>
    field.details.examQuestion && field.details.type !== "Text" && isFieldVisible(field));
  const savedQuestionCount = availableQuestions.filter(field =>
    String(state.savedAnswers[field._id] ?? "").trim() !== "").length;

  const getField = (type, item) => {
    let field = null;

    if (!isFieldVisible(item)) {
      return null;
    }

    if (type === "Textfield") {
      field = (
        <TextFieldElement
          key={item._id}
          req={item.details.required}
          id={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
          placeholder={item.details.placeholderText}
          value={state.formValues[item._id] || ""}
          handleChange={handleInputChange}
          type="text"
        />
      );
    } else if (type === "Number") {
      field = (
        <TextFieldElement
          key={item._id}
          req={item.details.required}
          id={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
          placeholder={item.details.placeholderText}
          value={state.formValues[item._id] || ""}
          handleChange={handleInputChange}
          type="number"
        />
      );
    } else if (type === "Phone") {
      field = (
        <TextFieldElement
          key={item._id}
          req={item.details.required}
          id={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
          placeholder={item.details.placeholderText}
          value={state.formValues[item._id] || ""}
          handleChange={handleInputChange}
          type="tel"
        />
      );
    } else if (type === "TextArea") {
      field = (
        <TextAreaElement
          key={item._id}
          req={item.details.required}
          id={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
          placeholder={item.details.placeholderText}
          value={state.formValues[item._id] || ""}
          handleChange={handleInputChange}
        />
      );
    } else if (type === "CheckBox") {
      field = (
        <CheckFieldsElement
          key={item._id}
          req={item.details.required}
          id={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
          selectionList={item.details.selectionList}
          value={state.formValues[item._id] || ""}
          handleChange={handleInputChange}
          formId={state.formId}
          fieldId={item._id}
          disabled={state.examExpired || state.loadingSubmit}
        />
      );
    } else if (type === "RadioButton") {
      field = (
        <RadioFieldsElement
          key={item._id}
          req={item.details.required}
          id={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
          selectionList={item.details.selectionList}
          value={state.formValues[item._id] || ""}
          handleChange={handleInputChange}
          formId={state.formId}
          fieldId={item._id}
        />
      );
    } else if (type === "File") {
      field = (
        <FormFileUploader
          key={item._id}
          title={item.title}
          subtitle={item.subtitle}
          req={item.details.required}
          desc={item.description}
          file={state.formValues[item._id] || null}
          formId={state.formId}
          type={item.details.fileType}
          setFile={(file) => {
            handleInputChange(
              item._id,
              file?.name ? `${baseURL}file/get/${file.name}` : ""
            ); 
          }}
        />
      );
    } else if (type === "Text") {
      field = (
        <TitleElement
          key={item._id}
          title={item.title}
          subtitle={item.subtitle}
          desc={item.description}
        />
      );
    }
    return field;
  };

  const getFormIcon = (icon) => {
    let formIcon = null;
    if (icon === "first") {
      formIcon = <MdAccountBalance fontSize={"50"} color="#FF9B14" />;
    } else if (icon === "second") {
      formIcon = <FaPen fontSize={"50"} color="#FF9B14" />;
    } else if (icon === "second") {
      formIcon = <FaPen fontSize={"50"} color="#FF9B14" />;
    } else if (icon === "third") {
      formIcon = <ListAltIcon style={{ color: "#FF9B14", fontSize: "50px" }} />;
    } else if (icon === "forth") {
      formIcon = (
        <HelpOutlineIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "fifth") {
      formIcon = <HelpIcon style={{ color: "#FF9B14", fontSize: "50px" }} />;
    } else if (icon === "sixth") {
      formIcon = (
        <ImportContactsIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "seventh") {
      formIcon = (
        <MenuBookIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "eighth") {
      formIcon = (
        <CreditCardIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "ninth") {
      formIcon = (
        <PermIdentityIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "thenth") {
      formIcon = <PostAddIcon style={{ color: "#FF9B14", fontSize: "50px" }} />;
    } else if (icon === "twelveth") {
      formIcon = (
        <CreateOutlinedIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "thirteenth") {
      formIcon = (
        <KeyboardArrowDownIcon style={{ color: "#FF9B14", fontSize: "50px" }} />
      );
    } else if (icon === "thirteenth") {
      formIcon = <HelpIcon style={{ color: "#FF9B14", fontSize: "50px" }} />;
    } else if (icon === "forteenth") {
      formIcon = (
        <ContactPhoneOutlinedIcon
          style={{ color: "#FF9B14", fontSize: "50px" }}
        />
      );
    } else if (icon === "fifteenth") {
      formIcon = (
        <ContactSupportOutlinedIcon
          style={{ color: "#FF9B14", fontSize: "50px" }}
        />
      );
    } else if (icon === "sixteenth") {
      formIcon = (
        <PermIdentityOutlinedIcon
          style={{ color: "#FF9B14", fontSize: "50px" }}
        />
      );
    } else if (icon === "seventeenth") {
      formIcon = (
        <LocalLibraryOutlinedIcon
          style={{ color: "#FF9B14", fontSize: "50px" }}
        />
      );
    } else if (icon === "eigtheen") {
      formIcon = (
        <BookmarkBorderOutlinedIcon
          style={{ color: "#FF9B14", fontSize: "50px" }}
        />
      );
    }
    return formIcon;
  };

  return (
    <AnimatedPageContainer>
      <div className="customContainer">
        {state.loading ? null : (
          <>
            <div className="w-100 d-flex justify-content-center pt-5 pb-3">
              {getFormIcon(state.formIcon)}
            </div>
            <p className={classes.title}>{state.formTitle}</p>
            <p className={classes.formSubtitle}>{state.formSubTitle}</p>
            {state.formPoster ? (
              <img
                src={`${baseURL}file/get/${state.formPoster}`}
                crossOrigin="anonymous"
                alt="poster"
                className="w-100 img-fluid my-4 borderRadius"
              />
            ) : null}
            <div
              className={classes.formDesc}
              dangerouslySetInnerHTML={{ __html: state.formDescription }}
            ></div>
            {/* <CustomButton
              text={"سوالات پرتکرار"}
              color={"primary"}
              clickHandler={() =>
                window.open(
                  "https://app.rahebehesht.com/?page_id=680",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              styles={{ marginBlock: "30px" }}
            /> */}
            {currentUser ? (
              <>
                <TitleElement title={"مشخصات فردی"} />
                <div className="mb-2">
                  <CustomInput
                    label={"نام و نام خانوادگی"}
                    value={currentUser.fullName}
                    disabled
                    name="name"
                  />
                </div>
                <div className="mb-2">
                  <CustomInput
                    label={"کد پرسنلی"}
                    value={currentUser.personnelCode}
                    disabled
                    name="name"
                  />
                </div>
                <div className="mb-2">
                  <CustomInput
                    label={"کد ملی"}
                    value={currentUser.nationalCode}
                    disabled
                    name="name"
                  />
                </div>
                <div className="mb-2">
                  <CustomInput
                    label={"شماره تلفن"}
                    value={currentUser.phone}
                    disabled
                    name="name"
                  />
                </div>
              </>
            ) : null}
          </>
        )}

        <fieldset disabled={state.examExpired || state.loadingSubmit} style={{ border: 0, padding: 0, margin: 0 }}>
          {state.loading ? (
            <div className="w-100 d-flex justify-content-center py-5 my-5">
              <Loading size={30} />
            </div>
          ) : state.formFields.length !== 0 ? (
            state.formFields.map((item, index) =>
              getField(item.details.type, item, index)
            )
          ) : // <NoResultAlter className={"w-100 py-5 my-5"} />
          null}
        </fieldset>
        {!state.loading && state.examTicket && (
          <p className={classes.examAnswerSummary} role="status" aria-live="polite" aria-atomic="true">
            {savedQuestionCount.toLocaleString("fa-IR")} از {availableQuestions.length.toLocaleString("fa-IR")} سوال پاسخ داده شده است.
          </p>
        )}
        {state.loadingSubmit ? (
          <div className="w-100 d-flex justify-content-center py-5 my-5">
            <Loading size={30} />
          </div>
        ) : (
          <CustomButton
            text={state.examExpired ? "ثبت پاسخ‌های ذخیره‌شده" : "ثبت"}
            color={"primary"}
            clickHandler={handleSubmit}
            styles={{ marginBlock: "30px" }}
          />
        )}
        {state.examTicket?.durationEnabled && state.remainingSeconds !== null ? (
          <div
            className={`${classes.examTimerFooter} ${
              state.examExpired ? classes.examTimerExpired : ""
            }`}
            role="timer"
            aria-live="polite"
          >
            <div className={classes.examTimerHeader}>
              <div className={classes.examTimerIdentity}>
                <span className={classes.examTimerIcon}>
                  <img src={TimerIcon} alt="" />
                </span>
                <div>
                  <p className={classes.examTimerTitle}>
                    {state.examExpired ? "پایان زمان آزمون" : "زمان باقی‌مانده"}
                  </p>
                  {!state.examExpired && state.saveStatus !== "idle" ? (
                    <p
                      className={`${classes.examSaveStatus} ${
                        state.saveStatus === "failed" ? classes.examSaveFailed : ""
                      }`}
                    >
                      {state.saveStatus === "saving"
                        ? "در حال ذخیره پاسخ..."
                        : state.saveStatus === "saved"
                        ? "پاسخ شما ذخیره شد"
                        : "پاسخ ذخیره نشد"}
                    </p>
                  ) : null}
                </div>
              </div>
              <strong className={classes.examTimerValue}>
                {formatRemainingTime(state.remainingSeconds)}
              </strong>
            </div>
            <div className={classes.examTimerProgress}>
              <ProgressBar
                progress={getRemainingTimeProgress()}
                progressColor={state.examExpired ? "var(--alertColor)" : "#5A9BFF"}
                height="8px"
              />
            </div>
          </div>
        ) : null}
      </div>
    </AnimatedPageContainer>
  );
};

export default FillForm;
